// CB:BEGIN realtor-signup-page
import React, { useState, useEffect } from 'react';
import { useHeadlessAuth, silentTicketSignIn } from '../auth/headlessSignup';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';
import SharedHeaderRealtor from '../components/SharedHeaderRealtor';
import { useDevice } from '../hooks/useDevice';

const BRAND_GREEN = '#123c3b';

const RealtorSignupPage: React.FC<{ onNavigate: (screen: string) => void }> = ({ onNavigate }) => {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { silentSignIn } = useHeadlessAuth();
  const { isMobileLayout } = useDevice();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [brokerageName, setBrokerageName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{ [k: string]: string }>({});
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeSms, setAgreeSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = getApiBaseUrl();

  // Clear stale role cache so switching roles works cleanly
  useEffect(() => { try { localStorage.removeItem('cb.usertype'); } catch {} }, []);

  // Already signed in as realtor → go to dashboard
  useEffect(() => {
    if (!isSignedIn || !user) return;
    if (localStorage.getItem('cb.usertype') === 'realtor') {
      onNavigate('realtor-dashboard');
    }
  }, [isSignedIn, user, onNavigate]);

  const validate = () => {
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!brokerageName.trim()) return 'Brokerage / Company Name is required';
    if (!email.includes('@')) return 'Valid email is required';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return 'Valid phone number is required';
    if (!agreeTerms) return 'You must agree to the Terms & Conditions';
    if (!agreePrivacy) return 'You must agree to the Privacy Policy';
    if (!isSignedIn) {
      // eslint-disable-next-line no-useless-escape
      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password);
      if (password.length < 8) return 'Password must be at least 8 characters';
      if (!/[A-Za-z]/.test(password)) return 'Password must contain at least one letter';
      if (!/\d/.test(password)) return 'Password must contain at least one number';
      if (!hasSpecial) return 'Password must contain at least one special character (!@#$%^&*)';
      if (password !== confirm) return 'Passwords do not match';
    }
    return null;
  };

  function handlePasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    if (name === 'password') setPassword(value); else setConfirm(value);
    const pwd = name === 'password' ? value : password;
    const conf = name === 'passwordConfirm' ? value : confirm;
    const next: { [k: string]: string } = {};
    if (pwd && conf && pwd !== conf) next.passwordConfirm = 'Passwords do not match';
    setPasswordErrors(prev => ({ ...prev, ...next, ...(next.passwordConfirm ? {} : { passwordConfirm: '' }) }));
  }

  async function handleSubmit() {
    if (busy) return;
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true);
    try {
      let clerkId = user?.id || '';

      // 1) Create Clerk account — backend sets publicMetadata.usertype='realtor' at creation time
      //    so that App.tsx provision sees the correct role before creating the DB user.
      if (!isSignedIn) {
        const resp = await fetch(`${apiBase.replace(/\/$/, '')}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phone,
            role: 'REALTOR',
            textOptIn: agreeSms,
          }),
        });
        if (!resp.ok) {
          let body: any = null;
          try { body = await resp.json(); } catch {}
          const msg = body?.message || body?.error || 'Registration failed. Please try again.';
          throw Object.assign(new Error(msg), { code: body?.error || 'register_failed' });
        }
        const data = await resp.json();
        clerkId = data.userId;

        // Cache role BEFORE setActive so App.tsx provision gate passes on first render
        try { localStorage.setItem('cb.usertype', 'realtor'); } catch {}

        // Sign in via sign-in ticket (no email verification required)
        let signed = await silentTicketSignIn(email, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone,
        });
        if (!signed) {
          // Fallback: direct password sign-in via Clerk frontend SDK
          try {
            const clerk: any = (window as any).Clerk;
            if (clerk) {
              await clerk.load();
              const si = await clerk.client.signIn.create({ identifier: email, password });
              if (si?.status === 'complete' && si?.createdSessionId) {
                await clerk.setActive({ session: si.createdSessionId });
                signed = true;
              }
            }
          } catch (e) {
            console.warn('[RealtorSignup] password sign-in fallback failed:', e);
          }
        }
      }

      // 2) Ensure DB user is REALTOR (idempotent)
      await fetch(`${apiBase.replace(/\/$/, '')}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: clerkId, email, account_type: 'REALTOR' }),
      }).catch(() => {});

      // 3) Save profile fields (fire-and-forget; user can complete from dashboard if token not ready yet)
      const token = typeof getToken === 'function' ? await getToken() : null;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${apiBase.replace(/\/$/, '')}/api/realtor/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          brokerageName: brokerageName.trim(),
          phone: phone.trim(),
          licenseNumber: licenseNumber.trim() || null,
        }),
      }).catch(() => {});
      // Agreement acceptance is NOT done automatically — the user must explicitly
      // review and sign from the Earnings & W-9 tab on the dashboard.

      localStorage.setItem('cb.usertype', 'realtor');
      localStorage.setItem('cb.realtor.onboardingComplete', 'true');
      onNavigate('realtor-dashboard');
    } catch (e: any) {
      const CODE_MESSAGES: Record<string, string> = {
        form_password_pwned:           'This password was found in a data breach. Please choose a different, unique password.',
        form_identifier_exists:        'An account with this email already exists. Try signing in instead.',
        form_password_too_short:       'Password must be at least 8 characters.',
        form_password_no_uppercase:    'Password must contain at least one uppercase letter.',
        form_password_no_lowercase:    'Password must contain at least one lowercase letter.',
        form_password_no_digit:        'Password must contain at least one number.',
        form_password_no_special_char: 'Password must contain at least one special character.',
        form_param_format_invalid:     'One or more fields are invalid. Please check your email and password.',
      };
      const friendly = CODE_MESSAGES[(e as any)?.code] ?? e?.message ?? 'Signup failed — please try again.';
      setError(friendly);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container">
      <style>{`
        #realtor-terms-checkbox, #realtor-privacy-checkbox, #realtor-sms-checkbox {
          width: 16px !important; height: 16px !important;
          min-width: 16px !important; min-height: 16px !important;
          max-width: 16px !important; max-height: 16px !important;
          transform: scale(0.75) !important; transform-origin: left center !important;
          appearance: none !important; -webkit-appearance: none !important;
          border: 1px solid #d1d5db !important; border-radius: 2px !important;
          background-color: white !important; cursor: pointer !important; margin: 0 !important;
        }
        #realtor-terms-checkbox:checked, #realtor-privacy-checkbox:checked, #realtor-sms-checkbox:checked {
          background-color: white !important; border-color: #d1d5db !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l3 3 6-6'/%3e%3c/svg%3e") !important;
          background-size: 100% 100% !important; background-position: center !important;
          background-repeat: no-repeat !important; box-shadow: none !important;
        }
      `}</style>

      <SharedHeaderRealtor onNavigate={onNavigate} />

      <div className="content">
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', marginBottom: 16 }}>Join as a Realtor</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* First / Last Name */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: 12 }}>
              <div>
                <label htmlFor="realtorFirstName" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>First Name *</label>
                <input
                  id="realtorFirstName"
                  className="form-control"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  placeholder="Jane"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label htmlFor="realtorLastName" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Last Name *</label>
                <input
                  id="realtorLastName"
                  className="form-control"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  placeholder="Smith"
                  autoComplete="family-name"
                />
              </div>
            </div>

            {/* Brokerage Name */}
            <div>
              <label htmlFor="brokerageName" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>
                Brokerage / Company Name *
              </label>
              <input
                id="brokerageName"
                className="form-control"
                value={brokerageName}
                onChange={e => setBrokerageName(e.target.value)}
                placeholder="e.g. Keller Williams — Jane Smith Team"
              />
            </div>

            {/* Email + Phone */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: 12 }}>
              <div>
                <label htmlFor="realtorEmail" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Email *</label>
                <input
                  id="realtorEmail"
                  className="form-control"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@brokerage.com"
                  type="email"
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="realtorPhone" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Phone *</label>
                <input
                  id="realtorPhone"
                  className="form-control"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="(555) 000-0000"
                  type="tel"
                />
              </div>
            </div>

            {/* License (optional) */}
            <div>
              <label htmlFor="licenseNumber" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>
                License # <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>(optional)</span>
              </label>
              <input
                id="licenseNumber"
                className="form-control"
                value={licenseNumber}
                onChange={e => setLicenseNumber(e.target.value)}
                placeholder="State license number"
              />
            </div>

            {/* Password — only shown when not already signed in */}
            {!isSignedIn && (
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Password *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={handlePasswordChange}
                    className="form-control"
                    placeholder="Create a password"
                    autoComplete="new-password"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(s => !s)}
                    className="btn btn-secondary"
                    style={{ padding: '8px 12px', fontSize: 12, minWidth: 60 }}
                  >
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
                <label style={{ display: 'block', marginBottom: 4, marginTop: 8, fontWeight: 600, fontSize: 14 }}>Confirm Password *</label>
                <input
                  type={showPw ? 'text' : 'password'}
                  name="passwordConfirm"
                  value={confirm}
                  onChange={handlePasswordChange}
                  className="form-control"
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                />
                {passwordErrors.passwordConfirm && (
                  <p style={{ color: '#dc2626', fontSize: 12, marginTop: 2 }}>{passwordErrors.passwordConfirm}</p>
                )}
              </div>
            )}

            {/* Checkboxes */}
            <div style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 400 }}>
                <input
                  id="realtor-terms-checkbox"
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={e => setAgreeTerms(e.target.checked)}
                  style={{ width: 16, height: 16, minWidth: 16, accentColor: BRAND_GREEN, cursor: 'pointer', margin: 0, flexShrink: 0 }}
                />
                <span style={{ color: '#1f2937' }}>I Agree to the <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> *</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 400 }}>
                <input
                  id="realtor-privacy-checkbox"
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={e => setAgreePrivacy(e.target.checked)}
                  style={{ width: 16, height: 16, minWidth: 16, cursor: 'pointer', margin: 0, flexShrink: 0 }}
                />
                <span style={{ color: '#1f2937' }}>I Agree to the <a href="/privacy" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a> *</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 400 }}>
                <input
                  id="realtor-sms-checkbox"
                  type="checkbox"
                  checked={agreeSms}
                  onChange={e => setAgreeSms(e.target.checked)}
                  style={{ width: 16, height: 16, minWidth: 16, cursor: 'pointer', margin: 0, flexShrink: 0 }}
                />
                <span style={{ color: '#1f2937' }}>I Agree to receive text messages</span>
              </label>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.3 }}>
                By entering your number & checking I agree to receive text messages, you agree to receive automated texts from CoreBids, Inc. at 877-570-7485 for referral updates and support. Msg frequency varies. Msg &amp; data rates may apply. Text STOP to cancel, HELP for help. See{' '}
                <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and{' '}
                <a href="/privacy" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </div>
            </div>

            {error && <div style={{ color: '#dc2626', fontSize: 12 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}
                style={{ background: BRAND_GREEN, borderColor: BRAND_GREEN }}>
                {busy ? 'Setting up your account…' : 'Get Started'}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorSignupPage;
// CB:END realtor-signup-page
